"use client";

import { QuotationConfirmationModal } from "@/components/quotation/quotation-confirmation-modal";
import { Dialog } from "@/components/ui/dialog";
import { PageHeroBannerWithBg } from "@/components/web-components/page-hero-banner-with-bg";
import { KSA } from "@/constants";
import { FlightTypeEnum } from "@/enum/flightType";
import { Link } from "@/i18n/navigation";
import { FlightData } from "@/interfaces/flightData";
import { haversineDistance } from "@/services/flight-service";
import { useFlightStore } from "@/store/flight-store";
import { addArrayElements } from "@/utils/common_functions";
import { FLIGHTS_DATA } from "@/utils/static_flight_data";
import moment from "moment";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useParams } from "next/navigation";
import { useEffect, useState } from "react";
import { AiOutlineUsergroupAdd } from "react-icons/ai";
import { FaCalendar, FaCheckCircle, FaClock, FaPlane } from "react-icons/fa";
import { GoArrowSwitch } from "react-icons/go";
import Loading from "../../loading";
import SingleFlightCard from "./components/SingleFlightCard";
import {
  findBestFlight,
  getSelectedAirportObject,
  getSummaryType,
} from "./estimate-price.service";
import { cn } from "@/lib/utils";

const EstimatePricePage = ({}) => {
  const { locale } = useParams();
  const [loading, setLoading] = useState<boolean>(true);
  const [flightToDisplay, setFlightToDisplay] = useState<
    FlightData | undefined
  >();
  const [searchQuery, setSearchQuery] = useState<any[]>([]);
  const [price, setPrice] = useState<number>(0);
  const [selectedFlightType, setSelectedFlightType] = useState("");
  const [totalDistance, setTotalDistance] = useState(0);
  const [recomendedQuote, setRecomendedQuote] = useState(0);
  const [isNotDomestic, setIsNotDomestic] = useState(false);
  const [airports, setAirports] = useState<any[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const t = useTranslations("estimatePage");

  // Zustand store for flight data
  const { quoteData, loadPersistedQuote } = useFlightStore();

  const fetchAirports = async () => {
    try {
      // Make an API call to fetch airport suggestions based on the input value
      const response = await fetch(`/api/v1/airport`);
      const data = await response.json();
      setAirports(data.data);
      // Don't process here - let the useEffect handle it when both data are available
    } catch (error) {
      // console.error("Error fetching airport suggestions:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    // Load persisted quote data from store
    loadPersistedQuote();
    fetchAirports();
  }, [loadPersistedQuote]);

  // Process data when both quoteData and airports are available
  useEffect(() => {
    if (quoteData && airports.length > 0) {
      queryData(airports);
    }
  }, [quoteData, airports]);

  const queryData = (list: any[]) => {
    // Use quote data from Zustand store instead of localStorage
    if (!quoteData) {
      setLoading(false);
      return;
    }

    let routes: any[];
    routes = quoteData.flights.map((route: any) => ({
      ...route,
      fromAirport: getSelectedAirportObject(route.fromAirport, list),
      toAirport: getSelectedAirportObject(route.toAirport, list),
      departureDate: moment(route.departureDate).format("DD MMM YYYY, HH:mm"),
      returnDate:
        route.returnDate &&
        moment(route.returnDate).format("DD MMM YYYY, HH:mm"),
    }));

    let isNotLocal: boolean = false;

    routes.forEach((element) => {
      return (isNotLocal =
        element?.fromAirport?.country !== KSA ||
        element?.toAirport?.country !== KSA);
    });

    setIsNotDomestic(isNotLocal);

    let flightType = quoteData.flightType;
    setSelectedFlightType(flightType);
    setSearchQuery(routes);
    let distances: number[] = [];
    let paxes: number[] = [];
    if (routes) {
      routes.forEach((trip: any) => {
        let distance = Math.round(
          haversineDistance(
            trip?.fromAirport?.lat,
            trip?.fromAirport?.lon,
            trip?.toAirport?.lat,
            trip?.toAirport?.lon
          )
        );
        paxes.push(trip.passengers);
        distances.push(distance);
      });
    }
    let pax = paxes.sort((a, b) => b - a)[0];

    let bestRecommendation: FlightData = findBestFlight(
      pax,
      isNotLocal ? FLIGHTS_DATA : FLIGHTS_DATA.slice(2)
    );
    let distance = Number(addArrayElements(distances));
    setTotalDistance(distance);
    let avgPrice: number;
    if (
      flightType === FlightTypeEnum.ONEWAY ||
      flightType === FlightTypeEnum.ROUNDWAY
    ) {
      avgPrice =
        ((distance * 2) / Number(bestRecommendation.avgSpeed)) *
        Number(bestRecommendation.price);
      setPrice(avgPrice);
    } else if (flightType === FlightTypeEnum.MULTICITY) {
      avgPrice =
        (distance / Number(bestRecommendation.avgSpeed)) *
        Number(bestRecommendation.price);
      setPrice(avgPrice);
    }
    getPrice(bestRecommendation, distance, flightType, routes);

    setFlightToDisplay(bestRecommendation);

    setLoading(false);
  };

  const getPrice = (
    data: FlightData,
    distance: number,
    type: string,
    routes: any[]
  ) => {
    let response: any;
    if (type === FlightTypeEnum.ONEWAY) {
      response = Math.floor(
        ((Number(distance) * 2) / Number(data?.avgSpeed)) * Number(data.price)
      );
      response = Math.floor(
        distance > Number(data.max_range) ? response + response * 0.1 : response
      ).toLocaleString();
    } else if (type === FlightTypeEnum.ROUNDWAY) {
      let days = moment(routes[0].returnDate).diff(
        moment(routes[0].departureDate),
        "days"
      );
      if (days > 2) {
        response = Math.round(
          ((Number(distance) * 2) / Number(data?.avgSpeed)) *
            Number(data.price) *
            2
        );
      } else {
        response = Math.round(
          ((Number(distance) * 2) / Number(data?.avgSpeed)) * Number(data.price)
        );
        response += response * 0.1;
      }
    } else if (type === FlightTypeEnum.MULTICITY) {
      response = Math.floor(
        (Number(distance) / Number(data?.avgSpeed)) * Number(data.price)
      );
      response = Math.floor(
        distance > Number(data.max_range) ? response + response * 0.1 : response
      ).toLocaleString();
    }

    setRecomendedQuote(response);
  };

  const breadcrumb = [
    {
      url: "/",
      name: locale === "en" ? "Home" : "الصفحة الرئيسية",
    },
    {
      url: "/estimate-price",
      name: t("estimate"),
    },
  ];

  return (
    <>
      {loading ? (
        <div className="min-h-screen bg-gray-100 grid place-content-center">
          <Loading />
        </div>
      ) : (
        <div className="min-h-screen bg-gray-100">
          <PageHeroBannerWithBg
            title={t("estimate")}
            breadcrumbItems={breadcrumb}
          />
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-0 my-14">
              <div className="font-light ltr:pr-10 rtl:pl-10">
                <h3 className="text-xl font-normal text-gray-700 mb-3">
                  {t("request")}
                </h3>
                <p className="text-base tracking-wide text-gray-500">
                  {t("req_desc")}
                </p>
              </div>
              <div className="font-light px-0 md:px-10 border:none md:border-l-2 md:border-r-2 border-gray-200">
                <h3 className="text-xl font-normal text-gray-700 mb-3">
                  {t("quote")}
                </h3>
                <p className="text-base tracking-wide text-gray-500">
                  {t("quote-desc")}
                </p>
              </div>
              <div className="font-light ltr:pl-0 rtl:pr-0 md:ltr:pl-10 md:rtl:pr-10 border:none md:border-l-2 md:border-r-2 border-gray-200">
                <h3 className="text-xl font-normal text-gray-700 mb-3">
                  {t("book")}
                </h3>
                <p className="text-base tracking-wide text-gray-500">
                  {t("book-desc")}
                </p>
              </div>
            </div>
            <div className="flex flex-col lg:flex-row gap-10 pb-15">
              <div className="w-full relative">
                <div
                  className={cn(
                    "flex items-center gap-2 absolute top-0  bg-white p-2  z-10",
                    locale === "en"
                      ? "left-0 rounded-tl-lg"
                      : "right-0 rounded-tr-lg"
                  )}
                >
                  <FaCheckCircle className="text-green-500 text-xl" />
                  <p className="text-green-500">
                    {locale === "en" ? "Recommended" : "موصى به"}
                  </p>
                </div>
                <div className="w-full rounded-xl bg-white flex flex-col md:flex-row ">
                  <div className="relative w-full min-h-[250px] md:h-auto md:w-80 h-auto rounded-lg overflow-hidden flex-shrink-0">
                    <Image
                      src={flightToDisplay?.image}
                      alt="Large Heavy Jet Image"
                      fill
                      className="object-fill"
                    />
                  </div>
                  <div className="flex flex-col w-full p-6">
                    <div className="flex justify-between items-start mb-4">
                      <h4 className="text-xl font-semibold text-gray-700 capitalize">
                        {flightToDisplay?.categoryName}
                      </h4>
                      <div className="text-end">
                        <p className="text-lg font-semibold">
                          $ {recomendedQuote}
                        </p>
                        {totalDistance > flightToDisplay.max_range &&
                          selectedFlightType !== FlightTypeEnum.ROUNDWAY && (
                            <p className="text-xs text-gray-500">
                              ({t("fuel")})
                            </p>
                          )}
                      </div>
                    </div>
                    <p className="text-gray-400 mb-4">
                      {flightToDisplay.jets.join(", ")}
                    </p>
                    <ul className="flex flex-wrap gap-4 mb-6">
                      <li className="flex items-center gap-2 text-gray-400">
                        <AiOutlineUsergroupAdd />
                        <span>
                          {flightToDisplay.min_capicity} -{" "}
                          {flightToDisplay.max_capicity}
                        </span>
                      </li>
                      <li className="flex items-center gap-2 text-gray-400">
                        <FaPlane />
                        <span>
                          {flightToDisplay.min_range} -{" "}
                          {flightToDisplay.max_range} nm
                        </span>
                      </li>
                      <li className="flex items-center gap-2 text-gray-400">
                        <FaClock />
                        <span>
                          {flightToDisplay.min_flight_hours} -{" "}
                          {flightToDisplay.max_flight_hours} hours
                        </span>
                      </li>
                    </ul>
                    <hr className="border-gray-200 mb-6" />
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mt-auto">
                      <div className="bg-gray-50 text-gray-400 text-xs p-3 rounded max-w-xs">
                        <p className="text-red-500">{t("price-warn")}</p>
                      </div>
                      <button
                        onClick={(e) => setIsOpen(true)}
                        className=" min-w-max outline-0 border-2 border-[#5a558e] bg-transparent font-medium text-[#5a558e] px-5 py-2.5 rounded-full hover:bg-[#5a558e] hover:text-white transition-colors"
                      >
                        {t("check")}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <div className="w-full lg:max-w-sm rounded-xl bg-white p-4 lg:p-6 flex flex-col">
                <h3 className="text-lg font-normal mb-2">
                  {t("summary")}{" "}
                  <span className="text-sm text-gray-400">
                    ({getSummaryType(selectedFlightType)})
                  </span>
                </h3>
                <div className="flex-1">
                  {searchQuery.map((query, index) => {
                    return (
                      <div className="mb-6" key={index}>
                        <div className="flex items-center justify-between gap-4 text-sm">
                          <div className="flex-1">
                            <p className="text-base font-semibold text-gray-500">
                              {query?.fromAirport?.code}
                            </p>
                            <p className="text-xs text-gray-400 rtl:text-right ltr:text-left">
                              {query?.fromAirport?.name}
                            </p>
                          </div>
                          <div className="flex-shrink-0">
                            <GoArrowSwitch className="text-gray-400" />
                          </div>
                          <div className="flex-1">
                            <p className="text-base font-semibold text-gray-500">
                              {query?.toAirport?.code}
                            </p>
                            <p className="text-xs text-gray-400 rtl:text-left ltr:text-right">
                              {query?.toAirport?.name}
                            </p>
                          </div>
                        </div>
                        <hr className="border-gray-200 my-3" />
                        <div className="flex justify-between items-center text-gray-500 text-xs">
                          <div className="flex items-center gap-2">
                            <FaCalendar />
                            <span dir="ltr">
                              {moment(query?.departure).format(
                                "MMM DD, YY hh:mm:A"
                              )}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <AiOutlineUsergroupAdd />
                            <span>{query?.passengers}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  <div className="flex justify-center mt-auto">
                    <Link
                      href="/"
                      className="w-full bg-[#5a558e] border-0 text-white text-center py-2 px-4 rounded text-sm font-medium hover:bg-[#4a4a7a] transition-colors"
                    >
                      {t("search")}
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Other Flights Section */}
            <section className="pb-20">
              <h2 className="mb-10 text-2xl font-semibold text-gray-700 px-4 md:px-0">
                {t("other-flight")}
              </h2>
              <div className="grid gap-10 grid-cols-1 md:grid-cols-2 lg:grid-cols-3 px-4 md:px-0">
                {/* Single Flight Card */}
                {(isNotDomestic ? FLIGHTS_DATA : FLIGHTS_DATA.slice(2))
                  .filter(
                    (el) => el.categoryName !== flightToDisplay.categoryName
                  )
                  .map((flight: FlightData, index: number) => {
                    return (
                      <SingleFlightCard
                        key={index}
                        flight={flight}
                        distance={totalDistance}
                        type={selectedFlightType}
                        route={searchQuery}
                      />
                    );
                  })}
              </div>
            </section>
          </div>
        </div>
      )}

      {isOpen && (
        <Dialog onClose={() => setIsOpen(false)}>
          <QuotationConfirmationModal
            query={searchQuery}
            flight={flightToDisplay}
            price={recomendedQuote}
          />
        </Dialog>
      )}
    </>
  );
};

export default EstimatePricePage;
